/*
 * ExteletBuildSurface — the ExteLet "Govern (Build)" surface (Prove 4).
 *
 * Implements EXTELET_7PROVE_JOURNEY.md §5 (LOCKED 2026-06-10): a single
 * Build→Skills surface with TWO regions —
 *   Region 1: Capability catalog (read-only) — discovered from the connector
 *             or added manually; the operator reviews/persists, never authors
 *             runnable skills (an ExteLet runs off-platform).
 *   Region 2: Governance Policy Skill(s) (editable) — the single, versioned
 *             enforcement object; edit → Save & Re-materialize.
 * Plus the BUILD-gate Integration-tests card (live probe + manual record).
 *
 * Extracted out of the 5,000-line AgentDetailPageV2.jsx so the ExteLet journey
 * (W1 fold here; W3 Decide / W5 Execute next) can be built and tested in
 * isolation without touching the native Let/AutoLet/SysLet surfaces. This is
 * the W1 finish: the old standalone "Skill mappings" card (with its misleading
 * "moving to the unified card above" note) is replaced by Region 1 here.
 *
 * Self-contained: owns its own form/probe state + persistence calls. Props are
 * the registration row + the shared discover state (lifted to the parent so the
 * page-level "Discover capabilities" button and this surface share one list).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Search, Trash2, ArrowRight, Network, Code2 } from 'lucide-react';
import { Card, Button, Pill } from '../';
import PhaseGate from '../PhaseGate';
import ExteletPolicySkills from './ExteletPolicySkills';
import ExteletConnectorSkills from './ExteletConnectorSkills';
import { useToast } from '../../../contexts/ToastContext';
import * as api from '../../../services/api';
import { usePlatform } from '../../../contexts/PlatformContext';

// V371 — action/reach capability vocabulary (mirrors backend SkillCapabilityClassifier).
const CAP_TAGS = ['read', 'write', 'modify', 'delete', 'revoke', 'send', 'external', 'network', 'destructive'];

/* JSONB {} ↔ [] guard (DEF-GEL-FE-NONARRAY-001): return the first array-shaped
 * candidate, else []. The backend may serialise an empty JSONB column as {}. */
function pickArrayExt(...candidates) {
  for (const c of candidates) if (Array.isArray(c)) return c;
  return [];
}

export default function ExteletBuildSurface({
  agent,
  registration,
  reloadRegistration,
  /* Shared discover state — owned by the parent so the page-level button and
   * this surface render the same discovered-but-unsaved list. */
  discoveryRunning,
  discoveredSkills = [],
  setDiscoveredSkills,
  handleDiscoverInline,
  /* DEF-EXTELET-ADVANCE-001 — lifted Build→Decide promote handler + current
   * phase, so this surface can render its own gated advance button (the native
   * BuildTab kept it; the ExteLet surface had lost it). */
  onAdvancePhase,
  agentPhase,
}) {
  const toastCtx = useToast();
  const addToast = useCallback((arg, type, duration) => {
    if (toastCtx && typeof toastCtx.addToast === 'function') return toastCtx.addToast(arg, type, duration);
    return null;
  }, [toastCtx]);

  // ── Build → Decide gate (REAL, backend-driven via previewPhaseGate) ──
  // displayed == enforced: the same checks ExternalAgentLifecycleService.
  // advanceToBuild enforces. Reuses the standalone PhaseGate component (a
  // genuine leaf, not BuildTab).
  const [gateChecks, setGateChecks] = useState([]);
  const [gateLoading, setGateLoading] = useState(true);
  const reloadGate = useCallback(async () => {
    if (!agent?.id) return;
    setGateLoading(true);
    try {
      const res = await api.previewPhaseGate?.(agent.id);
      const checks = Array.isArray(res?.data?.checks) ? res.data.checks : [];
      setGateChecks(checks);
    } catch { setGateChecks([]); }
    finally { setGateLoading(false); }
  }, [agent?.id]);
  useEffect(() => { reloadGate(); }, [reloadGate]);

  // ── Region 1 — capability catalog state ──────────────────────────────
  const [manualSkillForm, setManualSkillForm] = useState({ name: '', description: '', inputSchema: '' });
  const [manualSkillSubmitting, setManualSkillSubmitting] = useState(false);
  // Build→Decide advance in-flight guard (declared with the other hooks so it
  // never sits behind the early `return null`).
  const [advancing, setAdvancing] = useState(false);

  // Live-vs-sample tool execution + connected data connectors (tools auto-derive from them).
  const platform = usePlatform();
  const connectors = Array.isArray(platform?.connectors) ? platform.connectors : [];
  const [execBackend, setExecBackend] = useState(null);            // null until loaded
  const [execBackendSaving, setExecBackendSaving] = useState(false);
  const [assignConnectorId, setAssignConnectorId] = useState('');
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [assignments, setAssignments] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!agent?.id) return;
      try {
        const res = await api.getExecutionBackend?.(agent.id);
        if (alive && res?.data?.executionBackend) setExecBackend(res.data.executionBackend);
      } catch { /* defaults to sample data on error */ }
    })();
    return () => { alive = false; };
  }, [agent?.id]);

  // Reuse the shared platform connector list (same source + shape as the Connectors page).
  const loadConnectors = platform?.loadConnectors;
  useEffect(() => {
    if (typeof loadConnectors === 'function') loadConnectors();
  }, [loadConnectors]);

  // The connectors currently assigned to this agent — the source of its live tools.
  const reloadAssignments = useCallback(async () => {
    if (!agent?.id) return;
    try {
      const res = await api.getAgentConnectorAssignments?.(agent.id);
      const list = Array.isArray(res?.data) ? res.data
        : (Array.isArray(res?.data?.assignments) ? res.data.assignments : []);
      setAssignments(list);
    } catch { /* leave as-is */ }
  }, [agent?.id]);
  useEffect(() => { reloadAssignments(); }, [reloadAssignments]);

  const handleSetBackend = async (backend) => {
    if (!agent?.id || execBackendSaving || backend === execBackend) return;
    setExecBackendSaving(true);
    try {
      const res = await api.setExecutionBackend?.(agent.id, backend);
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Update failed',
          description: res.error || res.data?.error || `HTTP ${res.status || '?'}` });
        return;
      }
      setExecBackend(res?.data?.executionBackend || backend);
      addToast({ tone: 'success',
        title: backend === 'connector' ? 'Tool calls now use the live connector' : 'Tool calls now use sample data' });
    } catch (err) {
      addToast({ tone: 'danger', title: 'Update failed', description: err?.message || String(err) });
    } finally {
      setExecBackendSaving(false);
    }
  };

  const handleAssignConnector = async () => {
    if (!agent?.id || !assignConnectorId) return;
    setAssignSubmitting(true);
    try {
      const res = await api.createAgentConnectorAssignment?.(agent.id, { connectorId: assignConnectorId });
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Connect failed',
          description: res.error || res.data?.error || `HTTP ${res.status || '?'}` });
        return;
      }
      const cname = connectors.find(c => c.id === assignConnectorId)?.name || 'connector';
      addToast({ tone: 'success', title: `Connected ${cname} — its tools are now available` });
      setAssignConnectorId('');
      await reloadAssignments();
      await reloadRegistration?.();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Connect failed', description: err?.message || String(err) });
    } finally {
      setAssignSubmitting(false);
    }
  };

  const handleRemoveAssignment = async (assignmentId) => {
    if (!agent?.id || !assignmentId) return;
    try {
      const res = await api.removeAgentConnectorAssignment?.(agent.id, assignmentId);
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Disconnect failed',
          description: res.error || res.data?.error || `HTTP ${res.status || '?'}` });
        return;
      }
      addToast({ tone: 'success', title: 'Disconnected' });
      await reloadAssignments();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Disconnect failed', description: err?.message || String(err) });
    }
  };

  // V371 — toggle a capability tag on a discovered-but-unsaved skill.
  const toggleDiscoveredTag = (idx, tag) => {
    const next = discoveredSkills.map((s, i) => {
      if (i !== idx) return s;
      const cur = Array.isArray(s.capabilityTags) ? s.capabilityTags
        : (Array.isArray(s.suggestedTags) ? s.suggestedTags : []);
      const tags = cur.includes(tag) ? cur.filter((t) => t !== tag) : [...cur, tag];
      return { ...s, capabilityTags: tags };
    });
    setDiscoveredSkills?.(next);
  };

  const handlePersistDiscovered = async () => {
    if (!registration?.id || discoveredSkills.length === 0) return;
    try {
      const existing = pickArrayExt(registration.skillMappings);
      const byName = new Map();
      for (const s of existing) {
        const key = s?.name || s?.skillName;
        if (key) byName.set(key, s);
      }
      for (const s of discoveredSkills) {
        const key = s?.name || s?.skillName;
        if (!key) continue;
        // V371 — persist the (author-confirmed) capability tags; fall back to the
        // auto-suggested set when the author left them untouched.
        const capabilityTags = Array.isArray(s.capabilityTags) ? s.capabilityTags
          : (Array.isArray(s.suggestedTags) ? s.suggestedTags : []);
        byName.set(key, { ...byName.get(key), ...s, capabilityTags, discoveredFromPlatform: true });
      }
      const merged = Array.from(byName.values());
      const res = await api.gelUpdateSkillMappings?.(registration.id, merged);
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Save failed',
          description: res.error || res.data?.error || `HTTP ${res.status || '?'}` });
        return;
      }
      addToast({ tone: 'success', title: 'Capabilities saved',
        description: `${merged.length} total capability mapping(s) on file` });
      setDiscoveredSkills?.([]);
      await reloadRegistration?.();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Save failed', description: err?.message || String(err) });
    }
  };

  const handleAddManualSkill = async () => {
    if (!registration?.id || !manualSkillForm.name.trim()) return;
    setManualSkillSubmitting(true);
    try {
      const existing = pickArrayExt(registration.skillMappings);
      let inputSchemaObj = { type: 'object', properties: {} };
      const rawSchema = (manualSkillForm.inputSchema || '').trim();
      if (rawSchema) {
        try { inputSchemaObj = JSON.parse(rawSchema); }
        catch (e) {
          addToast({ tone: 'danger', title: 'Invalid input schema',
            description: 'Must be valid JSON: ' + e.message });
          setManualSkillSubmitting(false);
          return;
        }
      }
      const updated = [...existing, {
        name: manualSkillForm.name.trim(),
        description: manualSkillForm.description.trim(),
        inputSchema: inputSchemaObj,
      }];
      const res = await api.gelUpdateSkillMappings?.(registration.id, updated);
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Add failed',
          description: res.error || res.data?.error || `HTTP ${res.status || '?'}` });
        return;
      }
      addToast({ tone: 'success', title: `Capability "${manualSkillForm.name.trim()}" added` });
      setManualSkillForm({ name: '', description: '', inputSchema: '' });
      await reloadRegistration?.();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Add failed', description: err?.message || String(err) });
    } finally {
      setManualSkillSubmitting(false);
    }
  };

  const handleRemoveSkill = async (idx) => {
    if (!registration?.id) return;
    const existing = [...pickArrayExt(registration.skillMappings)];
    if (idx < 0 || idx >= existing.length) return;
    const removed = existing.splice(idx, 1)[0];
    try {
      const res = await api.gelUpdateSkillMappings?.(registration.id, existing);
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Remove failed',
          description: res.error || res.data?.error || `HTTP ${res.status || '?'}` });
        return;
      }
      addToast({ tone: 'info', title: 'Capability removed',
        description: `"${removed?.name || removed?.skillName || 'mapping'}" no longer attached` });
      await reloadRegistration?.();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Remove failed', description: err?.message || String(err) });
    }
  };

  // ── Region 2 — runtime bindings (read-only reflection of governance) ──
  const [bindings, setBindings] = useState(null);
  const [bindingsLoading, setBindingsLoading] = useState(false);
  const reloadBindings = useCallback(async () => {
    if (!registration?.id) return;
    setBindingsLoading(true);
    try {
      const fn = api['gelGetUnifiedEnforcementView'];
      if (typeof fn !== 'function') { setBindings(false); return; }
      const res = await fn(registration.id);
      if (res && res.ok === false) { setBindings(false); return; }
      const data = res?.data || res;
      setBindings(data && typeof data === 'object' ? data : false);
    } catch {
      setBindings(false);
    } finally {
      setBindingsLoading(false);
    }
  }, [registration?.id]);
  useEffect(() => { reloadBindings(); }, [reloadBindings]);

  // ── BUILD-gate integration tests ─────────────────────────────────────
  const [testForm, setTestForm] = useState({ testName: '', status: 'PASSED', details: '' });
  const [testSubmitting, setTestSubmitting] = useState(false);
  const [probeRunning, setProbeRunning] = useState(false);

  const handleRecordTest = async () => {
    if (!registration?.id) return;
    if (!testForm.testName.trim()) {
      addToast({ tone: 'danger', title: 'Test name required' });
      return;
    }
    setTestSubmitting(true);
    try {
      const detailsObj = {
        notes: testForm.details || 'Integration connectivity test',
        timestamp: new Date().toISOString(),
      };
      const res = await api.gelRecordIntegrationTest?.(
        registration.id, testForm.testName.trim(), testForm.status, detailsObj);
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Record failed',
          description: res.error || res.data?.error || `HTTP ${res.status || '?'}` });
        return;
      }
      addToast({ tone: 'success', title: 'Test recorded' });
      setTestForm({ testName: '', status: 'PASSED', details: '' });
      await reloadRegistration?.();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Record failed', description: err?.message || String(err) });
    } finally {
      setTestSubmitting(false);
    }
  };

  if (!registration || !registration.id) return null;

  const mappings = pickArrayExt(registration.skillMappings);
  const tests = pickArrayExt(registration.integrationTestResults, registration.integrationTests);

  // ── Governance enforcement model (from the registration's integration path) ──
  // 'CODED' → SDK Decorator (Java @Governed / Python @governed) is the live
  // enforcement point; 'MCP' (default) → the Governed MCP Gateway proxy.
  const integrationPath = registration.integrationPath || 'MCP';
  const isCodedPath = integrationPath === 'CODED';

  // ── Build → Decide gate criteria (mapped from the backend checks) ──
  const gateLabel = (c) => {
    switch (c.check) {
      case 'at_least_one_skill':        return 'Agent has at least 1 policy skill configured';
      case 'at_least_one_eval_ready':   return 'At least one policy skill is eval-ready (completed eval + passing score)';
      case 'registration_exists':       return 'External agent registration record exists';
      case 'skills_remaining_in_build': return 'Policy skills remaining in Build (informational)';
      case 'failed_evals_warning':      return 'Failed eval suites (informational)';
      default:                          return c.check || 'Backend check';
    }
  };
  const scrollToGovernance = () => {
    const el = document.querySelector('[data-card-id="skill-mappings"]');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.classList.add('ring-2', 'ring-v2-action', 'rounded-v2-md');
      setTimeout(() => el.classList.remove('ring-2', 'ring-v2-action', 'rounded-v2-md'), 1500);
    }
  };
  const gateCriteria = gateChecks.map((c) => ({
    id: c.check,
    passed: !!c.passed,
    label: gateLabel(c),
    detail: c.message,
    remediation: (!c.passed && (c.check === 'at_least_one_skill' || c.check === 'at_least_one_eval_ready'))
      ? { label: c.check === 'at_least_one_skill' ? 'Add a policy skill' : 'Generate an eval', onClick: scrollToGovernance }
      : null,
  }));
  const advanceReady = !gateLoading && gateCriteria.length > 0 && gateCriteria.every((c) => c.passed);

  return (
    <>
      {/* ══ How this agent is governed — enforcement model from integration path ══ */}
      <Card padding="sm" className="mt-3" data-card-id="governance-model">
        <Card.Header title="How this agent is protected" />
        <Card.Body>
          <p className="text-[11.5px] text-v2-ink-muted mb-3 max-w-[680px]">
            This agent runs <strong>off-platform</strong>, so Prove7 enforces policy at one of two
            points depending on how it's wired. Only protected calls are evaluated, enforced and
            audit-sealed — anything it does outside the enforcement point isn't protected.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
            {/* MCP Gateway */}
            <div className={`border rounded-v2-md p-3 ${!isCodedPath ? 'border-v2-action bg-v2-action-soft' : 'border-v2-border-subtle'}`}>
              <div className="flex items-center gap-2 mb-1">
                <Network size={14} className="text-v2-ink-secondary" />
                <span className="text-[12.5px] font-semibold text-v2-ink">MCP Gateway</span>
                {!isCodedPath
                  ? <Pill tone="success" size="sm" className="ml-auto">active for this agent</Pill>
                  : <Pill tone="muted" size="sm" className="ml-auto">off-the-shelf</Pill>}
              </div>
              <p className="text-[11px] text-v2-ink-muted m-0">
                Proxy chokepoint. The agent routes its <span className="font-mono-v2">tools/call</span> through
                the gateway; policy is evaluated <strong>pre-call</strong> (allow / block / throttle) and the
                response <strong>post-call</strong> (DPG redact + audit seal). No code change in the agent.
              </p>
            </div>
            {/* SDK Decorator */}
            <div className={`border rounded-v2-md p-3 ${isCodedPath ? 'border-v2-action bg-v2-action-soft' : 'border-v2-border-subtle'}`}>
              <div className="flex items-center gap-2 mb-1">
                <Code2 size={14} className="text-v2-ink-secondary" />
                <span className="text-[12.5px] font-semibold text-v2-ink">SDK Decorator</span>
                {isCodedPath
                  ? <Pill tone="success" size="sm" className="ml-auto">active for this agent</Pill>
                  : <Pill tone="trust" size="sm" className="ml-auto">for Coded Agents</Pill>}
              </div>
              <p className="text-[11px] text-v2-ink-muted m-0">
                For agents you control the code of, wrap tool calls with the Prove7 SDK decorator
                (<span className="font-mono-v2">@Governed</span> Java / Spring AOP · <span className="font-mono-v2">@governed</span> Python).
                <strong> Pre-</strong> and <strong>post-</strong> tool-call hooks fire policy <strong>inline in the
                agent's own process</strong> — tighter, lower-latency, and it sees calls that never leave the agent.
              </p>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Live-vs-sample tool execution + tool → connector-action mapping */}
      <Card padding="sm" className="mt-3" data-card-id="execution-backend">
        <Card.Header title="How tool calls run" />
        <Card.Body>
          <p className="text-[11.5px] text-v2-ink-muted mb-3 max-w-[680px]">
            Choose what happens when this agent calls a tool. <strong>Sample data</strong> returns example
            results — useful while you set things up. <strong>Live connector</strong> runs the tool against the
            real connected system under policy, returning only what the user is allowed to see.
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant={execBackend === 'demo' ? 'primary' : 'ghost'} size="sm"
              disabled={execBackendSaving} onClick={() => handleSetBackend('demo')}>
              Sample data
            </Button>
            <Button
              variant={execBackend === 'connector' ? 'primary' : 'ghost'} size="sm"
              disabled={execBackendSaving} onClick={() => handleSetBackend('connector')}>
              Live connector
            </Button>
            {execBackend && (
              <Pill tone={execBackend === 'connector' ? 'success' : 'muted'} size="sm" className="ml-1">
                {execBackend === 'connector' ? 'Live connector' : 'Sample data'}
              </Pill>
            )}
          </div>

          {execBackend === 'connector' && (
            <div className="mt-3 border-t border-v2-border-subtle pt-3">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-2">
                Connected data connectors
              </div>
              <p className="text-[11.5px] text-v2-ink-muted mb-2 max-w-[680px]">
                Connect a data connector and the agent's tools are derived automatically from what that
                connector provides — no per-tool setup. Configure connectors on the Connectors page first.
              </p>

              {assignments.length > 0 && (
                <ul className="space-y-1 mb-2 max-w-[560px]">
                  {assignments.map((a) => {
                    const c = connectors.find(cc => cc.id === a.connectorId);
                    return (
                      <li key={a.id} className="flex items-center justify-between border border-v2-border-subtle rounded-v2-sm px-2 py-1">
                        <span className="text-[12.5px] text-v2-ink">
                          {c?.name || a.connectorId || 'Connector'}{c?.type ? ` · ${c.type}` : ''}
                        </span>
                        <Button variant="ghost" size="sm" onClick={() => handleRemoveAssignment(a.id)}>Disconnect</Button>
                      </li>
                    );
                  })}
                </ul>
              )}

              {/* Inline per-connector action picker: tick a connected connector's actions to
                  add them as governed connector-bound skills (add/remove any time). */}
              {assignments.map((a) => {
                const c = connectors.find((cc) => cc.id === a.connectorId);
                return (
                  <ExteletConnectorSkills
                    key={`connskills-${a.id}`}
                    connector={c}
                    agent={agent}
                    addToast={addToast}
                    onChanged={() => { reloadRegistration?.(); reloadGate(); }}
                  />
                );
              })}

              <div className="flex gap-2 max-w-[560px]">
                <select
                  className="v2-focus-ring flex-1 bg-v2-surface border border-v2-border rounded-v2-sm px-2 py-1 text-[12.5px]"
                  value={assignConnectorId}
                  onChange={(e) => setAssignConnectorId(e.target.value)}
                  disabled={assignSubmitting}
                >
                  <option value="">Select a connector…</option>
                  {connectors
                    .filter((c) => !assignments.some((a) => a.connectorId === c.id))
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name || c.id}{c.type ? ` · ${c.type}` : ''}
                      </option>
                    ))}
                </select>
                <Button
                  variant="primary" size="sm"
                  onClick={handleAssignConnector}
                  disabled={!assignConnectorId || assignSubmitting}
                  loading={assignSubmitting}
                >
                  {assignSubmitting ? 'Connecting…' : 'Connect'}
                </Button>
              </div>
              {connectors.length === 0 && (
                <p className="text-[11px] text-v2-ink-muted mt-1">No connectors — configure one on the Connectors page first.</p>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* ══ Build → Decide gate · real PhaseGate — only while the agent is in
          Build. previewPhaseGate returns the CURRENT phase's checks, so past
          Build it would render the Decide/Act gate mislabeled here; those gates
          live on their own tabs (ExteletDecideTab). ══ */}
      {(agentPhase || agent?.phase || 'build').toLowerCase() === 'build' && (
      <div className="mt-3">
        <PhaseGate
          phase="BUILD"
          criteria={gateCriteria}
          advanceAction={
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-v2-ink-muted tabular-nums">
                Gate: {gateCriteria.filter((c) => c.passed).length} of {gateCriteria.length}
              </span>
              <Button
                variant="primary" size="sm"
                disabled={!advanceReady || advancing}
                loading={advancing}
                onClick={async () => {
                  setAdvancing(true);
                  try { await onAdvancePhase?.('Decide'); await reloadGate(); }
                  finally { setAdvancing(false); }
                }}
                title={advanceReady
                  ? 'Promote this ExteLet to the Decide phase'
                  : 'Gate not passing — add a governance policy skill and run a passing eval on it'}>
                Advance to Decide
              </Button>
            </div>
          }
        />
      </div>
      )}

      {/* ══ Build → Skills: one surface, two regions (journey §5) ══════ */}
      <Card padding="sm" className="mt-3" data-card-id="skill-mappings">
        <Card.Header
          title="Skills — capabilities & protection"
          actions={
            <Button
              variant="primary" size="sm"
              iconLeft={<Search size={13} />}
              onClick={handleDiscoverInline}
              disabled={discoveryRunning}
              loading={discoveryRunning}
            >
              {discoveryRunning ? 'Discovering…' : 'Discover skills'}
            </Button>
          }
        />
        <Card.Body>
          {/* ── Region 1 · Discover Skills (read-only catalog, optional) ── */}
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[11px] uppercase tracking-wider font-semibold text-v2-ink-muted">
              Discover Skills
            </span>
            <Pill tone="muted" size="sm">Optional</Pill>
          </div>
          <p className="text-[11.5px] text-v2-ink-muted mb-3">
            The tools this agent can call <strong>through Prove7</strong>, pulled from what its
            connector / MCP server advertises. You don't author or run these — cataloging them just
            lets you attach <strong>per-skill</strong> policy later. <strong>If the agent advertises
            nothing, that's fine</strong> — your protection policy still applies to every protected call.
          </p>

          {/* Discovered-but-unsaved review banner */}
          {discoveredSkills.length > 0 && (
            <div className="border border-v2-action rounded-v2-md p-3 mb-3 bg-v2-action-soft">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[12.5px] font-semibold text-v2-action-strong">
                  {discoveredSkills.length} newly discovered capability(ies) — review and save
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setDiscoveredSkills?.([])}>Discard</Button>
                  <Button variant="primary" size="sm" onClick={handlePersistDiscovered}>Save mappings</Button>
                </div>
              </div>
              <ul className="space-y-1 max-h-[180px] overflow-auto">
                {discoveredSkills.map((s, i) => {
                  const tags = Array.isArray(s.capabilityTags) ? s.capabilityTags
                    : (Array.isArray(s.suggestedTags) ? s.suggestedTags : []);
                  return (
                    <li key={i} className="text-[11.5px] py-1">
                      <div className="flex items-center gap-2">
                        <Pill tone="cyan" size="sm">discovered</Pill>
                        <span className="font-semibold text-v2-ink">{s.skillName || s.name}</span>
                        <span className="text-v2-ink-muted font-mono-v2">{s.endpoint || s.path || ''}</span>
                      </div>
                      <div className="flex items-center gap-1 flex-wrap mt-1 pl-1">
                        <span className="text-[10px] uppercase tracking-wider text-v2-ink-muted mr-1">capabilities</span>
                        {CAP_TAGS.map((t) => (
                          <button type="button" key={t} onClick={() => toggleDiscoveredTag(i, t)}
                            className={'text-[10.5px] rounded-v2-pill px-2 py-0.5 border ' + (tags.includes(t)
                              ? 'bg-v2-action-soft border-v2-action text-v2-action-strong font-semibold'
                              : 'bg-v2-surface border-v2-border text-v2-ink-muted')}>{t}</button>
                        ))}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Manual capability add (collapsed) */}
          <details className="mb-3 border border-v2-border rounded-v2-md p-2">
            <summary className="text-[12px] font-semibold text-v2-ink-secondary cursor-pointer select-none">
              + Add a skill manually
              <span className="font-normal text-v2-ink-muted"> — rare; only to document a tool the connector doesn't advertise</span>
            </summary>
            <div className="mt-2 space-y-2">
              <input
                className="v2-focus-ring w-full bg-v2-surface border border-v2-border rounded-v2-sm px-2 py-1 text-[12.5px]"
                placeholder="Capability name (e.g. summarize_document)"
                value={manualSkillForm.name}
                onChange={(e) => setManualSkillForm(f => ({ ...f, name: e.target.value }))}
                disabled={manualSkillSubmitting}
              />
              <input
                className="v2-focus-ring w-full bg-v2-surface border border-v2-border rounded-v2-sm px-2 py-1 text-[12.5px]"
                placeholder="Description (optional)"
                value={manualSkillForm.description}
                onChange={(e) => setManualSkillForm(f => ({ ...f, description: e.target.value }))}
                disabled={manualSkillSubmitting}
              />
              <textarea
                rows={3}
                className="v2-focus-ring w-full bg-v2-surface border border-v2-border rounded-v2-sm px-2 py-1 text-[11.5px] font-mono-v2"
                placeholder={'Input schema JSON (optional — e.g. {"type":"object","properties":{"text":{"type":"string"}}})'}
                value={manualSkillForm.inputSchema}
                onChange={(e) => setManualSkillForm(f => ({ ...f, inputSchema: e.target.value }))}
                disabled={manualSkillSubmitting}
              />
              <div className="flex justify-end">
                <Button
                  variant="primary" size="sm"
                  onClick={handleAddManualSkill}
                  disabled={!manualSkillForm.name.trim() || manualSkillSubmitting}
                  loading={manualSkillSubmitting}
                >
                  {manualSkillSubmitting ? 'Adding…' : 'Add capability'}
                </Button>
              </div>
            </div>
          </details>

          {/* Persistent capability list */}
          {mappings.length === 0 && discoveredSkills.length === 0 ? (
            <p className="text-v2-ink-muted text-[12.5px]">
              No skills discovered yet — that's a valid state. Your protection policy still applies to
              every protected call. Run discovery above, or add one manually only if you need per-skill policy.
            </p>
          ) : (
            <ul className="space-y-2 text-[12.5px]">
              {mappings.map((s, i) => (
                <li key={i} className="border-l-2 border-v2-border-subtle pl-3 flex items-baseline gap-2">
                  <Pill tone={s?.discoveredFromPlatform ? 'cyan' : 'muted'} size="sm">
                    {s?.discoveredFromPlatform ? 'discovered' : 'manual'}
                  </Pill>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-v2-ink">{s?.skillName || s?.name}</div>
                    {s?.description && <div className="text-[11.5px] text-v2-ink-muted">{s.description}</div>}
                    {(s?.endpoint || s?.path) && (
                      <div className="text-[10.5px] text-v2-ink-muted font-mono-v2 truncate">{s.endpoint || s.path}</div>
                    )}
                  </div>
                  {s?.riskLevel && (
                    <Pill
                      tone={s.riskLevel === 'HIGH' ? 'danger' : s.riskLevel === 'MEDIUM' ? 'warning' : 'muted'}
                      size="sm"
                    >
                      {s.riskLevel}
                    </Pill>
                  )}
                  <Button
                    variant="ghost" size="sm"
                    iconLeft={<Trash2 size={12} />}
                    onClick={() => handleRemoveSkill(i)}
                    title="Remove this capability mapping"
                  />
                </li>
              ))}
            </ul>
          )}

          {/* ── Region 2 · Governance Policy Skill(s) ── */}
          <div className="my-4 border-t border-v2-border-subtle" />
          <div className="text-[11px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-2">
            Region 2 · Protection Policy Skills
          </div>
          <p className="text-[11.5px] text-v2-ink-muted mb-3">
            Protection is the editable, versioned object that <em>is</em> this agent's enforcement —
            rate limit, classification, autonomy cap, approval, patterns, custom rules. Configure it,
            then run an <strong>eval</strong> to clear the Build → Decide gate.
          </p>
          <ExteletPolicySkills agent={agent} onChanged={() => { reloadGate(); reloadBindings(); }} />

          {/* Read-only runtime bindings — what's actually firing */}
          {bindingsLoading && (
            <div className="mt-4 text-[11.5px] text-v2-ink-muted italic">
              Loading runtime bindings…
            </div>
          )}
          {!bindingsLoading && bindings && typeof bindings === 'object' && (
            <div className="mt-4 pt-3 border-t border-v2-border-subtle">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-2">
                Runtime bindings
              </div>
              {(() => {
                const bridgeRules = pickArrayExt(bindings.bridgeRules);
                const frameworkBindings = pickArrayExt(bindings.frameworkBindings);
                const proxyConfigs = pickArrayExt(bindings.proxyConfigs);
                const totalAttached = bridgeRules.length + frameworkBindings.length + proxyConfigs.length;
                if (totalAttached === 0) {
                  return (
                    <p className="text-[11.5px] text-v2-ink-muted">
                      Nothing materialised yet. Save a Protection Policy Skill above to push rules through the bridge.
                    </p>
                  );
                }
                const activeRuleCount = proxyConfigs.reduce(
                  (sum, c) => sum + (Number(c.activeRuleCount) || 0), 0);
                return (
                  <>
                    <div className="flex flex-wrap gap-2 text-[11.5px] mb-2">
                      <Pill tone="cyan" size="sm">
                        {bridgeRules.length} bridge rule{bridgeRules.length === 1 ? '' : 's'}
                      </Pill>
                      <Pill tone="trust" size="sm">
                        {frameworkBindings.length} framework binding{frameworkBindings.length === 1 ? '' : 's'}
                      </Pill>
                      <Pill tone="success" size="sm">
                        {proxyConfigs.length} proxy skill config{proxyConfigs.length === 1 ? '' : 's'}
                      </Pill>
                      {activeRuleCount > 0 && (
                        <Pill tone="muted" size="sm">
                          {activeRuleCount} active rule{activeRuleCount === 1 ? '' : 's'}
                        </Pill>
                      )}
                    </div>
                    {bridgeRules.length > 0 && (
                      <div className="mb-2">
                        <div className="text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">
                          Bridge rules
                        </div>
                        <ul className="space-y-1 text-[11.5px]">
                          {bridgeRules.slice(0, 5).map((r, i) => (
                            <li key={i} className="flex items-baseline gap-2">
                              <Pill
                                tone={r.severity === 'HIGH' || r.severity === 'CRITICAL' ? 'danger'
                                  : r.severity === 'MEDIUM' ? 'warning' : 'muted'}
                                size="sm"
                              >
                                {r.severity || 'INFO'}
                              </Pill>
                              <span className="font-semibold text-v2-ink">{r.name || r.ruleId}</span>
                              {r.ruleType && (
                                <span className="text-[10.5px] text-v2-ink-muted">{r.ruleType}</span>
                              )}
                            </li>
                          ))}
                          {bridgeRules.length > 5 && (
                            <li className="text-[10.5px] text-v2-ink-muted italic">
                              …and {bridgeRules.length - 5} more
                            </li>
                          )}
                        </ul>
                      </div>
                    )}
                    {frameworkBindings.length > 0 && (
                      <div className="mb-2">
                        <div className="text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">
                          Framework bindings
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {frameworkBindings.map((fb, i) => (
                            <Pill key={i} tone="trust" size="sm">
                              {fb.frameworkCode || fb.frameworkName || fb.frameworkId}
                            </Pill>
                          ))}
                        </div>
                      </div>
                    )}
                    {proxyConfigs.length > 0 && (
                      <div>
                        <div className="text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">
                          Proxy skill configs
                        </div>
                        <ul className="space-y-0.5 text-[11.5px]">
                          {proxyConfigs.map((c, i) => (
                            <li key={i} className="flex items-baseline gap-2">
                              <Pill
                                tone={c.activationStatus === 'ACTIVE' ? 'success' : 'muted'}
                                size="sm"
                              >
                                {c.activationStatus || 'INACTIVE'}
                              </Pill>
                              <span className="font-semibold text-v2-ink">{c.skillName}</span>
                              {Number(c.activeRuleCount) > 0 && (
                                <span className="text-[10.5px] text-v2-ink-muted">
                                  {c.activeRuleCount} rule(s)
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* ══ BUILD gate · Integration tests ════════════════════════════ */}
      <Card padding="sm" className="mt-3" data-card-id="integration-tests">
        <Card.Header
          title={`Integration tests · ${tests.length}`}
          actions={
            <Button
              variant="primary" size="sm"
              iconLeft={<Search size={13} />}
              onClick={async () => {
                setProbeRunning(true);
                try {
                  const res = await api.gelRunIntegrationProbe(registration.id);
                  if (res?.ok === false) {
                    addToast({ tone: 'danger', title: 'Probe failed',
                      description: res.error || `HTTP ${res.status || '?'}` });
                    return;
                  }
                  addToast({ tone: 'success', title: 'Probe completed',
                    description: 'Integration test row recorded — see list below.' });
                  reloadRegistration?.();
                } catch (err) {
                  addToast({ tone: 'danger', title: 'Probe failed',
                    description: err?.message || String(err) });
                } finally {
                  setProbeRunning(false);
                }
              }}
              disabled={probeRunning}
              loading={probeRunning}
            >
              {probeRunning ? 'Probing…' : 'Run live probe'}
            </Button>
          }
        />
        <Card.Body>
          <p className="text-[11.5px] text-v2-ink-muted mb-3">
            Optional connectivity evidence. <strong>Run live probe</strong> fires a real exchange against the
            gateway (records 2xx as PASSED), or record a manual test below. The Build → Decide gate is now
            <strong> eval-readiness</strong> (see the gate above), not integration tests.
          </p>

          <div className="border border-v2-border-subtle rounded-v2-md p-3 mb-4">
            <div className="text-[12.5px] font-semibold text-v2-ink mb-2">Record test result</div>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <input
                type="text"
                value={testForm.testName}
                onChange={(e) => setTestForm({ ...testForm, testName: e.target.value })}
                placeholder="Test name (e.g., 'Search query returns results')"
                className="v2-focus-ring text-[12px] px-2 py-1.5 border border-v2-border rounded-v2-sm w-full bg-v2-surface text-v2-ink"
                disabled={testSubmitting}
              />
              <select
                value={testForm.status}
                onChange={(e) => setTestForm({ ...testForm, status: e.target.value })}
                className="v2-focus-ring text-[12px] px-2 py-1.5 border border-v2-border rounded-v2-sm w-full bg-v2-surface text-v2-ink"
                disabled={testSubmitting}
              >
                <option value="PASSED">PASSED</option>
                <option value="FAILED">FAILED</option>
              </select>
            </div>
            <textarea
              value={testForm.details}
              onChange={(e) => setTestForm({ ...testForm, details: e.target.value })}
              placeholder="Test details / prompt / expected vs. actual…"
              className="v2-focus-ring text-[11.5px] px-2 py-1.5 border border-v2-border rounded-v2-sm w-full font-mono-v2 bg-v2-surface text-v2-ink"
              rows={3}
              disabled={testSubmitting}
            />
            <div className="flex justify-end mt-2">
              <Button
                variant="primary" size="sm"
                onClick={handleRecordTest}
                disabled={testSubmitting || !testForm.testName.trim()}
                loading={testSubmitting}
              >
                {testSubmitting ? 'Recording…' : 'Record result'}
              </Button>
            </div>
          </div>

          {tests.length === 0 ? (
            <p className="text-v2-ink-muted text-[12.5px]">No tests recorded yet.</p>
          ) : (
            <ul className="space-y-2 text-[12.5px]">
              {tests.map((t, i) => {
                const result = t?.status || t?.result;
                const stamp = t?.timestamp || t?.recordedAt;
                return (
                  <li key={i} className="flex items-baseline gap-2">
                    <Pill tone={result === 'PASSED' ? 'success' : 'danger'} size="sm" dot>
                      {result || 'UNKNOWN'}
                    </Pill>
                    <div className="flex-1 min-w-0">
                      <div className="text-v2-ink font-semibold">{t?.testName || '(unnamed test)'}</div>
                      {(t?.details?.notes || t?.details) && (
                        <div className="text-[11px] text-v2-ink-muted truncate">
                          {typeof t.details === 'string' ? t.details : (t.details?.notes || JSON.stringify(t.details))}
                        </div>
                      )}
                    </div>
                    {stamp && (
                      <span className="text-v2-ink-muted text-[10.5px] font-mono-v2">
                        {new Date(stamp).toLocaleString()}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {/* Advance control now lives in the real PhaseGate above (Build→Decide
            * is eval-readiness, not integration tests). */}
        </Card.Body>
      </Card>
    </>
  );
}
